import Clarity from "@microsoft/clarity";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { CLARITY_PROJECT_ID } from "../../project_config";
import Breadcrumb from "../common/breadcrumb/Breadcrumb";
import Footer from "../common/footer/Footer";
import Header from "../common/header/Header";

const WHY_ITEMS = [
	{
		title: "تخصص در حوزه‌های مختلف",
		body: "دسترسی به متخصصان روان‌شناسی و مشاوره در زمینه‌های متنوع زندگی فردی و خانوادگی.",
	},
	{
		title: "انتخاب متناسب با نیاز شما",
		body: "کمک به شناخت بهتر مسئله و انتخاب خدمات متناسب با شرایط و دغدغه‌های شما.",
	},
	{
		title: "دسترسی آسان",
		body: "امکان استفاده از خدمات مشاوره آنلاین و حضوری، متناسب با شرایط و ترجیحات شما.",
	},
	{
		title: "یادگیری و توانمندسازی",
		body: "دوره‌ها و کارگاه‌های کاربردی برای افزایش آگاهی و تقویت مهارت‌های روان‌شناختی.",
	},
	{
		title: "همراهی در مسیر تغییر",
		body: "از شناخت بهتر خود تا بهبود روابط و تصمیم‌گیری‌های مهم زندگی، در کنار متخصصان این مسیر را آگاهانه‌تر طی کنید.",
	},
];

function WhyCard({ index, title, body }) {
	return (
		<div className="rounded-[16px] border border-[#E2E8F0] bg-white p-5 md:p-6 shadow-sm hover:border-[#276AA6]/30 hover:shadow-md transition-all">
			<div className="flex items-start gap-3">
				<span
					className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lightCta font-Vazir-700 text-[15px] text-darkBlue"
					aria-hidden
				>
					{index}
				</span>
				<div className="min-w-0 text-right">
					<h3 className="font-Vazir-700 text-[16px] md:text-[17px] text-darkBlue mb-2">
						{title}
					</h3>
					<p className="font-Vazir-400 text-[14px] md:text-[15px] leading-[1.9] text-[#64748B]">
						{body}
					</p>
				</div>
			</div>
		</div>
	);
}

export default function AboutUs() {
	Clarity.init(CLARITY_PROJECT_ID);
	const [isDesktop] = useState(() => window.innerWidth > 768);

	const linkDataList = [
		{ label: "خانه", href: "/" },
		{ label: "درباره ما", href: "/about_us/" },
	];

	return (
		<>
			<Helmet>
				<title>درباره ما | همراهتیم</title>
				<meta
					name="description"
					content="درباره همراهتیم — همراه شما برای شناخت، آگاهی و تغییر در حوزه روان‌شناسی و مشاوره."
				/>
			</Helmet>

			<div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className={`${isDesktop ? "w-[600px]" : ""} mx-auto`}>
							<Breadcrumb linkDataList={linkDataList} />
						</div>
					</div>

					<div className="spacer p-[32px] md:p-[40px]" />

					<div
						className={`${isDesktop ? "max-w-[840px]" : "px-4"} mx-auto space-y-8 md:space-y-10`}
					>
						<section className="rounded-[16px] bg-gradient-to-l from-[#276AA6] to-[#1e5280] px-6 py-10 md:px-10 md:py-12 text-center text-white shadow-sm">
							<p className="font-Vazir-400 text-[13px] md:text-[14px] text-white/80 mb-3">
								چرا همراهتیم؟
							</p>
							<h1 className="font-Vazir-800 text-[22px] md:text-[28px] leading-snug mb-4">
								متخصص مناسب، مسیر روشن‌تر، انتخاب آگاهانه‌تر
							</h1>
							<p className="font-Vazir-400 text-[14px] md:text-[15px] leading-relaxed text-white/90 max-w-xl mx-auto">
								همراهتیم برای ساده‌تر کردن دسترسی به خدمات تخصصی سلامت روان و
								همراهی شما در مسیر شناخت و تغییر شکل گرفته است.
							</p>
						</section>

						<section aria-labelledby="why-heading">
							<h2 id="why-heading" className="sr-only">
								چرا همراهتیم؟
							</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
								{WHY_ITEMS.map((item, i) => (
									<WhyCard
										key={item.title}
										index={i + 1}
										title={item.title}
										body={item.body}
									/>
								))}
							</div>
						</section>

						<section
							className="bg-white rounded-[16px] shadow-sm p-6 md:p-10 border border-[#E2E8F0]"
							aria-labelledby="about-heading"
						>
							<h2
								id="about-heading"
								className="font-Vazir-800 text-[22px] md:text-[26px] text-darkBlue text-center mb-2"
							>
								درباره همراهتیم
							</h2>
							<h3 className="font-Vazir-700 text-[16px] md:text-[18px] text-[#475569] text-center mb-8">
								همراهتیم؛ همراه شما برای شناخت، آگاهی و تغییر
							</h3>
							<div className="font-Vazir-400 text-[15px] md:text-[16px] leading-[2.1] text-[#4B5155] text-right space-y-5">
								<p>
									همراهتیم، مجموعه‌ای تخصصی در حوزه روان‌شناسی و مشاوره است که با
									هدف آسان‌تر کردن دسترسی به خدمات تخصصی سلامت روان شکل گرفته
									است.
								</p>
								<p>
									ما باور داریم مراجعه به متخصص روان‌شناسی فقط به زمان بروز
									مشکلات جدی محدود نمی‌شود. شناخت بهتر خود، بهبود روابط، تربیت
									فرزند، مدیریت احساسات و تصمیم‌گیری درباره مسائل مهم زندگی نیز
									می‌توانند به آگاهی و راهنمایی تخصصی نیاز داشته باشند.
								</p>
								<p>
									در همراهتیم، تلاش می‌کنیم دسترسی شما به متخصصان حوزه‌های مختلف،
									از جمله روان‌شناسی کودک و نوجوان، مسائل فردی و خانوادگی،
									روابط زوجین و مسائل جنسی، ساده‌تر و متناسب با نیازتان باشد.
								</p>
								<p>
									همچنین با ارائه خدمات مشاوره آنلاین و حضوری و فراهم کردن
									امکان استفاده از دوره‌ها و کارگاه‌های آموزشی، می‌خواهیم مسیر
									دسترسی به دانش تخصصی و مهارت‌های کاربردی روان‌شناختی را
									آسان‌تر کنیم.
								</p>
								<p>
									هدف همراهتیم، فراهم کردن فضایی برای انتخاب آگاهانه‌تر، شناخت
									عمیق‌تر و برداشتن قدم‌های مؤثرتر در مسیر زندگی است.
								</p>
								<p className="font-Vazir-700 text-darkBlue text-center pt-2 border-t border-[#E2E8F0] mt-6">
									همراهتیم؛ برای بهتر فهمیدن خودمان و رابطه‌هایمان.
								</p>
							</div>
						</section>
					</div>

					<div className="spacer p-[32px] md:p-[40px]" />
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
